<template>
  <div>
    <!-- Row 1: Search -->
    <v-row align="center">
      <v-col cols="12" md="8">
        <v-text-field
          v-model="localSearchTerm"
          :label="searchLabel"
          clearable
          density="compact"
          variant="outlined"
          prepend-inner-icon="mdi-magnify"
          hide-details="auto"
          @keyup.enter="$emit('search')"
          @click="$event.target.select()"
          @dblclick="localSearchTerm = ''"
        />
      </v-col>
      <v-col cols="12" md="4" class="d-flex align-center gap-2">
        <v-btn color="primary" @click="$emit('search')" :disabled="!canSearch">
          <v-icon class="mr-1">mdi-magnify</v-icon>
          {{ searchButtonText }}
        </v-btn>
        <v-btn color="secondary" variant="outlined" @click="reset">
          <v-icon class="mr-1">mdi-refresh</v-icon>
          Reset
        </v-btn>
      </v-col>
    </v-row>

    <!-- Row 2: Client Selection -->
    <v-row class="mt-2" align="center">
      <v-col cols="12" md="8">
        <ClientSearchFilter
          v-model="localClientId"
          v-model:include-subclients="localIncludeSubclients"
          :show-include-subclients="true"
          client-select-label="Client"
          @hierarchy-loaded="$emit('hierarchy-loaded')"
        />
      </v-col>
    </v-row>

    <!-- Row 3: Status Selection -->
    <v-row class="mt-2">
      <v-col cols="12">
        <div class="text-subtitle-2 font-weight-bold mb-2">Status SIM subscriber mobil</div>
        <v-chip-group v-if="useChips" v-model="localStatusId" selected-class="bg-primary">
          <v-chip value="all_except_terminated" variant="outlined" filter>
            Toate (Except terminate)
          </v-chip>
          <v-chip value="all" variant="outlined" filter>
            Toate
          </v-chip>
          <v-chip
            v-for="status in simStatuses"
            :key="status.id"
            :value="status.id"
            variant="outlined"
            filter
            :color="getStatusColor(status.name)"
          >
            {{ formatStatusLabel(status.name) }}
          </v-chip>
        </v-chip-group>
        <v-radio-group v-else v-model="localStatusId" inline hide-details density="compact">
          <v-radio label="Toate (fara terminate)" value="all_except_terminated"></v-radio>
          <v-radio label="Toate" value="all"></v-radio>
          <v-radio
            v-for="status in simStatuses"
            :key="status.id"
            :label="formatStatusLabel(status.name)"
            :value="status.id"
          ></v-radio>
        </v-radio-group>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import ClientSearchFilter from '@/components/ClientSearchFilter.vue'
import { getSimStatuses } from '@/composables/useGetSimsAPI'
import store from '@/store'

export default {
  name: 'SimSearchFilters',
  components: { ClientSearchFilter },
  props: {
    searchTerm: { type: String, default: '' },
    clientId: { type: [Number, String], default: null },
    includeSubclients: { type: Boolean, default: true },
    statusId: { type: [Number, String], default: 'all_except_terminated' },
    searchLabel: { type: String, default: 'IMSI, Serial, MSISDN, Seria device, NUI, Nume tech' },
    searchButtonText: { type: String, default: 'Cauta' },
    useChips: { type: Boolean, default: true },
    minSearchLength: { type: Number, default: 3 },
    alwaysAllowSearch: { type: Boolean, default: false }
  },
  emits: ['update:searchTerm', 'update:clientId', 'update:includeSubclients', 'update:statusId', 'search', 'reset', 'hierarchy-loaded'],
  data() {
    return {
      simStatuses: []
    }
  },
  computed: {
    token() {
      return store.getters.getToken
    },
    localSearchTerm: {
      get() { return this.searchTerm },
      set(val) { this.$emit('update:searchTerm', val) }
    },
    localClientId: {
      get() { return this.clientId },
      set(val) { this.$emit('update:clientId', val) }
    },
    localIncludeSubclients: {
      get() { return this.includeSubclients },
      set(val) { this.$emit('update:includeSubclients', val) }
    },
    localStatusId: {
      get() { return this.statusId },
      set(val) { this.$emit('update:statusId', val) }
    },
    canSearch() {
      if (this.alwaysAllowSearch) return true
      const realLength = this.searchTerm?.trim().replace(/\*/g, '').length || 0
      return (realLength >= this.minSearchLength) ||
             this.clientId ||
             (this.statusId && this.statusId !== 'all_except_terminated')
    }
  },
  async mounted() {
    await this.loadStatuses()
  },
  methods: {
    async loadStatuses() {
      const result = await getSimStatuses(this.token)
      if (!result.error && result.sim_statuses) {
        this.simStatuses = result.sim_statuses
      }
    },
    reset() {
      this.$emit('update:searchTerm', '')
      this.$emit('update:clientId', null)
      this.$emit('update:includeSubclients', true)
      this.$emit('update:statusId', 'all_except_terminated')
      this.$emit('reset')
    },
    getStatusColor(statusName) {
      const colors = {
        'active': 'success',
        'not_provisioned': 'info',
        'pending': 'warning',
        'suspended': 'orange',
        'terminated': 'grey'
      }
      return colors[statusName] || 'grey'
    },
    formatStatusLabel(statusName) {
      const labels = {
        'active': 'Active',
        'not_provisioned': 'Neprovizionate',
        'pending': 'In asteptare',
        'suspended': 'Suspendate',
        'terminated': 'Terminate'
      }
      return labels[statusName] || statusName
    }
  }
}
</script>
